/* -----------------------------------------------------------------------
 * <copyright company="Microsoft Corporation">
 *   Copyright (c) Microsoft Corporation.  All rights reserved.
 * </copyright>
 * ----------------------------------------------------------------------- */

import "./Navbar.scss";

import {
  OpenInNewTab,
} from "../constants";
import { Fabric, INavLinkGroup, Nav, getWindow } from "@fluentui/react";
import { firstNavStyles, secondNavStyles } from "./NavbarInBuiltStyle";

import React from "react";
import { getNavBarLinks } from "../staticLinks";
import { useWindowWidth } from "../util";

const _window = getWindow();
let eventAdded = false;

const NavbarCareersComponent: React.FunctionComponent = () => {
  const [canExpand, setCanExpand] = React.useState(false);
  const [groupExpanded, setGroupExpanded] = React.useState({} as keyValuePair);
  const view = useWindowWidth();

  interface keyValuePair {
    [key: string]: string | boolean;
  }

  if (!eventAdded) {
    eventAdded = true;
    if (_window) {
      _window.addEventListener("click", () => {
        setCanExpand(false);
        if (document.getElementsByClassName("responsive").length > 0) {
          document
            .getElementsByClassName("ms-Nav")[0]
            .classList.toggle("responsive");
        }
      });
    }
  }
  const onHeaderClick = (ev?: React.MouseEvent<HTMLElement>) => {
    const newGroupExpanded: keyValuePair = {};
    for (const key of Object.keys(groupExpanded)) {
      newGroupExpanded[key] = canExpand ? groupExpanded[key] : false;
    }
    if (ev) {
      const eventTargetElem = ev.target as HTMLElement;
      let keyName = "";
      if (eventTargetElem.childNodes.length > 1) {
        keyName = eventTargetElem.childNodes[1].textContent!; // eslint-disable-line
      } else {
        keyName = eventTargetElem.textContent!; // eslint-disable-line
      }

      newGroupExpanded[keyName] = !newGroupExpanded[keyName];
      setGroupExpanded(newGroupExpanded);
      setCanExpand(true);
      ev.stopPropagation();
    }
  };
  // eslint-disable-next-line
  const onRenderGroupHeader = (props: any, defaultRenderer: any) => {
    const links =
      canExpand && props.isExpanded && groupExpanded[props.name]
        ? props.links
        : [];
    return defaultRenderer({
      ...props,
      isExpanded: canExpand && groupExpanded[props.name],
      links: links,
    });
  };
  const _hrefs = getNavBarLinks();

  const onCareersHeaderClick = () => {
    if (view.width > 1096) {
      window.open(_hrefs[0].url, "_self");
    } else {
      document
        .getElementsByClassName("ms-Nav")[0]
        .classList.toggle("responsive");
    }
  }
  /* -------------------------------------------------------------------------------------------------------
   * Note: Under some of the Keys, Names and URLs dummy name is kept which just acts as a placeholder      *
   *       but has no impact on the application or code since they are mandatory fields and could not be   *
   *       skipped                                                                                         *
   * ------------------------------------------------------------------------------------------------------*/
  const firstCommonNavLinkGroups: INavLinkGroup[] = [
    {
      name: "Careers",
      collapseAriaLabel: "Careers",
      links: [],
      collapseByDefault: true,
      onHeaderClick: onCareersHeaderClick,
    },
    {
      name: "Home",
      collapseAriaLabel: "Home",
      collapseByDefault: true,
      links: [],
      onHeaderClick: () => {
        window.open(_hrefs[0].url, "_self");
      },
    },
    ...[
        {
          name:"Professionals",
          expandAriaLabel: "Professionals Dropdown",
          collapseAriaLabel: "Professionals",
          collapseByDefault: true,
          links:
            canExpand &&
              groupExpanded[
                "Professionals"
              ]
              ? [
                {
                  key: "label21",
                  name: "Home",
                  ariaLabel: "Home",
                  url: _hrefs[1].url,
                },
                {
                  key: "label22",
                  name: "Locations",
                  ariaLabel: "Locations",
                  url: _hrefs[2].url,
                },
                {
                  key: "label23",
                  name: "Professions",
                  ariaLabel: "Professions",
                  url: _hrefs[3].url,
                },
                {
                  key: "label24",
                  name: "US Military and Veterans",
                  ariaLabel: "US Military and Veterans",
                  url: _hrefs[4].url,
                },
              ]
              : [],
          onHeaderClick: onHeaderClick,
        },
      ],
    {
      name: "Students & graduates",
      collapseAriaLabel: "Students & graduates",
      collapseByDefault: true,
      links: [],
      onHeaderClick: () => {
        window.open(_hrefs[5].url, "_self");
      },
    },
    {
      name: "Life at Microsoft",
      expandAriaLabel: "Life at Microsoft Dropdown",
      collapseAriaLabel: "Life at Microsoft",
      collapseByDefault: true,
      links:
        canExpand &&
          groupExpanded[
          "Life at Microsoft"
          ]
          ? [
            {
              key: "label41",
              name: "Benefits",
              ariaLabel: "Benefits",
              url: _hrefs[6].url,
            },
            {
              key: "label42",
              name: "Culture",
              ariaLabel: "Culture",
              url: _hrefs[7].url,
            },
            {
              key: "label43",
              name: "Diversity and inclusion",
              ariaLabel: "Diversity and inclusion",
              url: _hrefs[8].url,
            },
            {
              key: "label44",
              name: "Microsoft Life",
              ariaLabel: "Microsoft Life",
              url: _hrefs[9].url,
              target: OpenInNewTab,
            }
          ]
          : [],
      onHeaderClick: onHeaderClick,
    },
  ];


  //Updating the logic for Nav internal links rendering due to async issue
  const firstNavLinkGroups = firstCommonNavLinkGroups;

  return (
    <div
      className="ac-ms-career-navbar-background"
      role="navigation"
      aria-label="main navigation"
    >
      <Nav
        styles={firstNavStyles}
        className={`ac-ms-first-nav`}
        ariaLabel="Nav Bar"
        groups={firstNavLinkGroups}
        onRenderGroupHeader={onRenderGroupHeader}
      />
      <Nav
        styles={secondNavStyles}
        className={`ac-ms-second-nav`}
        ariaLabel="Nav Bar"
        groups={[]}
        onRenderGroupHeader={onRenderGroupHeader}
      />
    </div>
  );
};

const NavbarCareersComponentWrapper: React.FunctionComponent = () => (
  <Fabric>
    <NavbarCareersComponent />
  </Fabric>
);
export default NavbarCareersComponentWrapper;
